import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { focusMainContent } from "@/components/route-announcer";
import AuthGrant from "./data-access/auth-grant";
import type { DataAccessQueueItem } from "@/types";
import AuthGrantDisclaimer from "./data-access/auth-grant-disclaimer";
import AuthRevokeDisclaimer from "./data-access/auth-revoke-disclaimer";
import AuthDeclined from "./data-access/auth-declined";
import AuthAccept from "./data-access/auth-accept";
import {
  APP_ALERT_MODULE,
  DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER,
} from "@/constants";
import useAuth, { AuthMode } from "@/context/auth-provider";
import { lowerCaseComparison } from "@/lib/string-util";

export const AppAlertDialog = () => {
  const { authData } = useAuth();
  const alertDialog = useSelector(
    (state: RootState) => state.appAlertDialog.appAlertDialogs[0],
  );
  const isVisible = Boolean(
    alertDialog && !lowerCaseComparison(authData?.mode, AuthMode.GUEST),
  );

  /**
   * 2.4.3 Focus Order — A: these dialogs aren't opened from a clickable trigger in
   * the current view — they appear from the Redux alert queue, and this component
   * unmounts back to null once the queue empties, whether that's from an immediate
   * Cancel/Decline click or an async mutation's onSuccess. Neither path is a normal
   * Radix open/close transition this component controls, so nothing reliably hands
   * focus back to the page — it was silently dropping to <body>. There's no
   * meaningful "previous" element to return to either, since nothing in the current
   * view triggered these — so focus goes to the page's own main content instead,
   * the same target a route change itself lands on, every time the queue empties.
   * Guarded so this doesn't fire on initial mount, when no dialog has shown yet.
   */
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (isVisible) {
      wasVisibleRef.current = true;
      return;
    }
    if (wasVisibleRef.current) {
      wasVisibleRef.current = false;
      focusMainContent();
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const renderAppAlertDialog = () => {
    switch (alertDialog.module) {
      // case APP_ALERT_MODULE.PAPERLESS_EOB:
      //   return <GoPaperlessEobAlert />;

      case APP_ALERT_MODULE.DATA_ACCESS:
        switch (alertDialog.contentType) {
          case DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.AUTH_GRANT:
            // request alert shown to loggedin user when dependent grants data access, loggedin user needs to either accept or decline grant request
            return (
              <AuthGrantDisclaimer
                contentType={
                  (
                    alertDialog as Omit<
                      DataAccessQueueItem,
                      "date" | "status" | "grantedBy"
                    >
                  ).contentType
                }
                grantedTo={
                  (
                    alertDialog as Omit<
                      DataAccessQueueItem,
                      "date" | "status" | "grantedBy"
                    >
                  ).grantedTo
                }
              />
            );
          case DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.AUTH_REVOKE:
            return (
              <AuthRevokeDisclaimer
                contentType={
                  (
                    alertDialog as Omit<
                      DataAccessQueueItem,
                      "date" | "status" | "grantedBy"
                    >
                  ).contentType
                }
                grantedTo={
                  (
                    alertDialog as Omit<
                      DataAccessQueueItem,
                      "date" | "status" | "grantedBy"
                    >
                  ).grantedTo
                }
              />
            );
          case DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.ACCEPTED:
            // acknowledgeable alert shown to loggedin user if grant request sent by the user is accepted by dependent
            return (
              <AuthAccept
                grantedTo={(alertDialog as DataAccessQueueItem).grantedTo}
                contentType={(alertDialog as DataAccessQueueItem).contentType}
              />
            );
          case DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.DECLINED:
            // acknowledgeable alert shown to loggedin user if grant request sent by the user is denied by dependent
            return (
              <AuthDeclined
                grantedTo={(alertDialog as DataAccessQueueItem).grantedTo}
                contentType={(alertDialog as DataAccessQueueItem).contentType}
              />
            );
          case DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.GRANTED:
            return (
              <AuthGrant
                grantedBy={(alertDialog as DataAccessQueueItem).grantedBy}
                contentType={(alertDialog as DataAccessQueueItem).contentType}
              />
            );
        }
    }
  };
  return renderAppAlertDialog();
};

export default AppAlertDialog;
